import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
import { COMPOUND_OPERATOR_AND } from '@atlaskit/jql-ast/compound';
import creators from '@atlaskit/jql-ast/creators';
import { OPERATOR_IN } from '@atlaskit/jql-ast/operator';
import { print } from '@atlaskit/jql-ast/printers';
import { JastBuilder } from '@atlaskit/jql-ast/jast-builder';
import {
	CLAUSE_TYPE_COMPOUND,
	CLAUSE_TYPE_TERMINAL,
	CLAUSE_TYPE_NOT,
} from '@atlaskit/jql-ast/clause';
import type { Clause } from '@atlaskit/jql-ast/ast/clause';

type ProjectKey = string;

const quoteProjectKey = (projectKey: ProjectKey): string => `"${projectKey}"`;

const PROJECT_FIELD_KEY = 'project';

export const getJQLForCollection = (projectsKeys: ProjectKey[] = [], filtersJql = ''): string => {
	const jast = new JastBuilder().build(filtersJql);
	projectsKeys.length &&
		jast.query?.appendClause(
			creators.terminalClause(
				creators.field(PROJECT_FIELD_KEY),
				creators.operator(OPERATOR_IN),
				creators.listOperand(projectsKeys.map(quoteProjectKey).map(creators.byText.valueOperand)),
			),
			COMPOUND_OPERATOR_AND,
		);

	if (jast.query?.orderBy) {
		jast.query.removeOrderBy();
	}

	return print(jast);
};

const projectInRegex = /project\s+in\s+\((.*?)\)/;
const projectKeyRegex = /"(.*?)"/g;

export const extractProjectKeysFromJql = (jql: string): string[] => {
	// Extract the part of the JQL related to `project in (...)`
	const projectInMatch = projectInRegex.exec(jql);

	const projectKeys: string[] = [];

	if (projectInMatch) {
		const projectInClause = projectInMatch[1];
		let match;

		// Extract all project keys inside the quotes within the `project in (...)` clause
		// eslint-disable-next-line no-cond-assign
		while ((match = projectKeyRegex.exec(projectInClause)) !== null) {
			projectKeys.push(match[1]);
		}
	}

	return projectKeys;
};

// extract the filters jql part of collection jql by removing the project keys jql clause
export const extractFiltersJqlFromJql = (jql: string): string => {
	const jast = new JastBuilder().build(jql);
	const { where } = jast.query ?? {};
	if (!where) return '';

	const clauses = where.clauseType === CLAUSE_TYPE_COMPOUND ? where.clauses : [where];
	const projectKeysClause = clauses.find(
		(c) => c.clauseType === CLAUSE_TYPE_TERMINAL && c.field.value === PROJECT_FIELD_KEY,
	);
	if (projectKeysClause) {
		projectKeysClause.remove();
	}

	return print(jast);
};

const getFieldsFromClauses = ([clause, ...rest]: Clause[]): FieldKey[] => {
	if (!clause) return [];

	if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
		return [clause.field.value, ...getFieldsFromClauses(rest)];
	}
	if (clause.clauseType === CLAUSE_TYPE_NOT) {
		return getFieldsFromClauses([clause.clause, ...rest]);
	}
	return getFieldsFromClauses([...clause.clauses, ...rest]);
};

type FieldKeysMap = { [name: string]: FieldKey };

const getFieldKeyFromFieldName = (fieldKeys: FieldKeysMap) => (fieldName: string) => {
	const fieldNameClean = fieldName.replace(/\[[^\]]+\]$/, '');
	return fieldKeys[fieldNameClean.toLowerCase()];
};

export const getFieldsFromJql = (jql: string, fieldKeys: FieldKeysMap): FieldKey[] => {
	const jast = new JastBuilder().build(jql);
	const { where } = jast.query ?? {};
	if (!where) return [];

	const clauses = where.clauseType === CLAUSE_TYPE_COMPOUND ? where.clauses : [where];
	return getFieldsFromClauses(clauses)
		.map(getFieldKeyFromFieldName(fieldKeys))
		.filter((field) => !!field);
};
